/**
 * Audit viewer store (T7 — plan §7.3).
 *
 * Hydrates from ``GET /api/projects/{id}/audit/timeline`` (NDJSON) via
 * ``fetch`` + ``ReadableStream`` so a long run renders rows
 * progressively rather than blocking on the full body. Verification
 * status comes from the cheaper ``/audit/verify`` endpoint and is
 * surfaced as a green/red badge in the viewer header.
 *
 * The store is *project-scoped*: ``hydrate(projectId, runId)`` clears
 * any previous state before kicking off a new fetch. Concurrent
 * navigations between two projects collapse into a single in-flight
 * stream (older tokens are discarded inside the reader loop).
 *
 * Why Zustand (not React Query)?
 *   The viewer wants to stream rows as they arrive — React Query's
 *   default caching model assumes an entire response is materialised
 *   before consumers see it. Zustand lets us update ``entries`` in
 *   place on every line without a re-key dance.
 */
import { create } from 'zustand'

import {
  llmCallFileUrl,
  type AuditEvent,
  type AuditVerifyResponse,
  type LLMCallFile,
} from '@/types/audit'

/**
 * Cached state for one persisted LLM call file. Keyed by the
 * ``call_path`` carried in the ``llm_usage`` event payload so a
 * second click on the same row is free.
 */
export interface LLMCallEntry {
  status: 'loading' | 'ready' | 'error'
  body?: LLMCallFile
  error?: string
}

export interface AuditState {
  /** Project the current entries belong to (null when nothing loaded). */
  projectId: string | null
  /** Run the current entries belong to. */
  runId: number | null
  /** Arrival-ordered timeline entries. */
  entries: AuditEvent[]
  /** Verify badge state. ``null`` = not yet checked. */
  verify: AuditVerifyResponse | null
  /** True while the NDJSON stream is open. */
  loading: boolean
  /** Any error from the most recent hydrate / verify. */
  error: string | null
  /** Currently-focused entry (used by the side panel). */
  selectedTs: string | null
  /** Cached LLM-call detail bodies, keyed by ``payload.call_path``. */
  llmCalls: Record<string, LLMCallEntry>

  /**
   * Stream the timeline for ``projectId/runId`` into the store.
   *
   * Returns the active fetch token so a caller can race two
   * navigations and only consume the latest.
   */
  hydrate: (projectId: string, runId: number) => Promise<void>
  /** Refresh the verify badge without re-streaming the timeline. */
  refreshVerify: () => Promise<void>
  /** Set / clear the side-panel selection. */
  selectEntry: (ts: string | null) => void
  /**
   * Fetch one ``audit/llm/<seq>_<agent>.json`` file and stash it in
   * :prop:`llmCalls`. Idempotent — a second call for the same path
   * is a no-op while a fetch is in flight, and a second call after
   * a fetch finished short-circuits to the cached entry.
   */
  fetchLLMCall: (callPath: string) => Promise<void>
  /** Drop everything — used on project change / unmount. */
  reset: () => void
}

// --------------------------------------------------------------------- //
// Concurrent-hydrate token. Module-scoped so a second hydrate call can
// invalidate an in-flight stream's writes after the user navigates
// away.
// --------------------------------------------------------------------- //
let _hydrateToken = 0

const initialState: Pick<
  AuditState,
  | 'projectId'
  | 'runId'
  | 'entries'
  | 'verify'
  | 'loading'
  | 'error'
  | 'selectedTs'
  | 'llmCalls'
> = {
  projectId: null,
  runId: null,
  entries: [],
  verify: null,
  loading: false,
  error: null,
  selectedTs: null,
  llmCalls: {},
}

export const useAuditStore = create<AuditState>((set, get) => ({
  ...initialState,

  reset: () => {
    _hydrateToken += 1
    set({ ...initialState })
  },

  selectEntry: (ts) => set({ selectedTs: ts }),

  fetchLLMCall: async (callPath) => {
    const state = get()
    const { projectId, runId } = state
    if (!projectId || runId === null) return
    const cached = state.llmCalls[callPath]
    // Already loading or already loaded → nothing to do.
    if (cached && cached.status !== 'error') return

    set((s) => ({
      llmCalls: { ...s.llmCalls, [callPath]: { status: 'loading' } },
    }))
    try {
      const resp = await fetch(llmCallFileUrl(projectId, runId, callPath))
      if (!resp.ok) {
        set((s) => ({
          llmCalls: {
            ...s.llmCalls,
            [callPath]: { status: 'error', error: `HTTP ${resp.status}` },
          },
        }))
        return
      }
      const body = (await resp.json()) as LLMCallFile
      set((s) => ({
        llmCalls: {
          ...s.llmCalls,
          [callPath]: { status: 'ready', body },
        },
      }))
    } catch (err) {
      set((s) => ({
        llmCalls: {
          ...s.llmCalls,
          [callPath]: { status: 'error', error: (err as Error).message },
        },
      }))
    }
  },

  refreshVerify: async () => {
    const { projectId } = get()
    if (!projectId) return
    try {
      const resp = await fetch(`/api/projects/${encodeURIComponent(projectId)}/audit/verify`)
      if (!resp.ok) {
        set({ verify: { ok: false, length: 0, diag: `HTTP ${resp.status}` } })
        return
      }
      const body = (await resp.json()) as AuditVerifyResponse
      set({ verify: body })
    } catch (err) {
      set({ verify: { ok: false, length: 0, diag: (err as Error).message } })
    }
  },

  hydrate: async (projectId, runId) => {
    // Take a fresh token so any in-flight previous stream stops
    // writing into our state once it notices the mismatch.
    _hydrateToken += 1
    const myToken = _hydrateToken
    set({
      projectId,
      runId,
      entries: [],
      verify: null,
      loading: true,
      error: null,
      selectedTs: null,
    })

    // Fire-and-forget verify in parallel — its result lands separately.
    void get().refreshVerify()

    try {
      const url =
        `/api/projects/${encodeURIComponent(projectId)}/audit/timeline?run_id=${encodeURIComponent(
          String(runId),
        )}`
      const resp = await fetch(url)
      if (!resp.ok) {
        if (myToken === _hydrateToken) {
          set({ loading: false, error: `HTTP ${resp.status}` })
        }
        return
      }
      const body = resp.body
      if (!body) {
        // Tiny body / no streaming support — fall back to text.
        const text = await resp.text()
        if (myToken !== _hydrateToken) return
        const parsed = parseNdjson(text)
        set({ entries: parsed, loading: false })
        return
      }

      const reader = body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''
      while (true) {
        const { done, value } = await reader.read()
        if (myToken !== _hydrateToken) {
          // A newer hydrate took over — drop this stream silently.
          try {
            await reader.cancel()
          } catch {
            // ignore
          }
          return
        }
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        // Keep the partial trailing line in the buffer for the next chunk.
        buffer = lines.pop() ?? ''
        const parsed: AuditEvent[] = []
        for (const line of lines) {
          const event = parseLine(line)
          if (event) parsed.push(event)
        }
        if (parsed.length > 0) {
          set((state) => ({ entries: [...state.entries, ...parsed] }))
        }
      }
      // Flush any trailing partial line.
      const tail = buffer.trim()
      if (tail) {
        const event = parseLine(tail)
        if (event && myToken === _hydrateToken) {
          set((state) => ({ entries: [...state.entries, event] }))
        }
      }
      if (myToken === _hydrateToken) {
        set({ loading: false })
      }
    } catch (err) {
      if (myToken === _hydrateToken) {
        set({ loading: false, error: (err as Error).message })
      }
    }
  },
}))

// --------------------------------------------------------------------- //
// Helpers
// --------------------------------------------------------------------- //
export function parseLine(line: string): AuditEvent | null {
  const trimmed = line.trim()
  if (!trimmed) return null
  try {
    const parsed = JSON.parse(trimmed) as AuditEvent
    if (typeof parsed?.event !== 'string' || typeof parsed?.ts !== 'string') {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function parseNdjson(body: string): AuditEvent[] {
  const out: AuditEvent[] = []
  for (const line of body.split('\n')) {
    const event = parseLine(line)
    if (event) out.push(event)
  }
  return out
}
